import { useEffect, useState } from 'react';
import axios from 'axios';
import JSZip from 'jszip';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Archive, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Competition Summary Pack — zips together the key documents for
 * the selected competition so the organiser has one downloadable
 * record before the comp is deleted.
 *
 * Pack contents (all default checked; ungated items can still be
 * unticked):
 *   1. Event Notice            (regenerated fresh from server)
 *   2. Print Version Programme (regenerated fresh from server)
 *   3. Entry Summary           (populated, all clubs)
 *   4. Summary of Results      (only available when results exist)
 *   5. Post Event Report       (only available when expenses persisted)
 *
 * Each generation step updates the same sticky sonner toast so the
 * user sees a live progress feed without a stack of dismissable
 * toasts. On success the zip downloads and the parent's
 * ``onArchiveCreated`` callback is fired so the page can open the
 * competition delete-confirmation dialog (owner spec — Feb 2026).
 */
export default function SummaryPackPanel({ competitionId, competition, onArchiveCreated }) {
  const [preflight, setPreflight] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [selections, setSelections] = useState({
    event_notice: true,
    programme: true,
    entry_summary: true,
    results: true,
    post_event_report: true,
  });

  useEffect(() => {
    if (!competitionId) {
      setPreflight(null);
      return;
    }
    setLoading(true);
    (async () => {
      try {
        const res = await axios.get(
          `${API}/documents/summary-pack/${competitionId}/preflight`,
          { withCredentials: true }
        );
        setPreflight(res.data);
        // Default the gated items to whatever's available.
        setSelections((s) => ({
          ...s,
          results: !!res.data.results_available,
          post_event_report: !!res.data.post_event_report_available,
        }));
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to load summary pack info');
      } finally {
        setLoading(false);
      }
    })();
  }, [competitionId]);

  const toggle = (k) => setSelections((s) => ({ ...s, [k]: !s[k] }));

  const getYearForFilename = () => {
    const sd = preflight?.competition_start_date || competition?.start_date;
    if (sd) {
      const m = String(sd).match(/^(\d{4})/);
      if (m) return m[1];
    }
    return String(new Date().getFullYear());
  };

  const fetchBlob = async (url, params = undefined, method = 'GET', body = undefined) => {
    const cfg = { withCredentials: true, responseType: 'blob', timeout: 240000 };
    if (params) cfg.params = params;
    const res = method === 'POST'
      ? await axios.post(url, body || {}, cfg)
      : await axios.get(url, cfg);
    return res.data;
  };

  // For zip entries that are themselves a ZIP (Post Event Report,
  // Summary of Results), we unwrap and re-add the inner files so the
  // final archive doesn't contain nested zips.
  const addBlobToZip = async (zip, name, blob) => {
    const isZip =
      blob.type === 'application/zip' ||
      blob.type === 'application/x-zip-compressed' ||
      name.toLowerCase().endsWith('.zip');
    if (isZip) {
      const inner = await JSZip.loadAsync(blob);
      await Promise.all(
        Object.entries(inner.files).map(async ([fname, entry]) => {
          if (entry.dir) return;
          const data = await entry.async('blob');
          zip.file(fname, data);
        })
      );
    } else {
      zip.file(name, blob);
    }
  };

  const filenameSafe = (s) =>
    String(s || '').replace(/[\\/:*?"<>|]/g, '-').replace(/\s+/g, ' ').trim();

  const generate = async () => {
    if (!competitionId) return;
    setGenerating(true);
    const compName = preflight?.competition_name || competition?.name || 'Competition';
    const year = getYearForFilename();
    const archiveFilename = `${year} ${filenameSafe(compName)} - Event Archive.zip`;

    // Single sticky toast that we update as each doc completes.
    const toastId = 'summary-pack-progress';
    toast.loading('Preparing summary pack…', { id: toastId });

    const zip = new JSZip();
    const steps = [];
    if (selections.event_notice) {
      steps.push({
        label: 'Event Notice',
        run: async () => {
          const blob = await fetchBlob(
            `${API}/documents/schedule-covering-letter`,
            { competition_id: competitionId },
            'POST',
          );
          await addBlobToZip(zip, `${filenameSafe(compName)} - Event Notice.docx`, blob);
        },
      });
    }
    if (selections.programme) {
      steps.push({
        label: 'Print Programme',
        run: async () => {
          const blob = await fetchBlob(
            `${API}/order-of-skate/${competitionId}/final-programme`,
          );
          await addBlobToZip(zip, `${filenameSafe(compName)} - Programme.docx`, blob);
        },
      });
    }
    if (selections.entry_summary) {
      steps.push({
        label: 'Entry Summary',
        run: async () => {
          const blob = await fetchBlob(
            `${API}/competitions/${competitionId}/entry-template`,
            { populated: true },
          );
          await addBlobToZip(zip, `${filenameSafe(compName)} - Entry Summary.xlsx`, blob);
        },
      });
    }
    if (selections.results && preflight?.results_available) {
      steps.push({
        label: 'Summary of Results',
        run: async () => {
          const blob = await fetchBlob(
            `${API}/results/${competitionId}/generate`,
            undefined, 'POST',
          );
          await addBlobToZip(zip, `${filenameSafe(compName)} - Summary of Results.xlsx`, blob);
        },
      });
    }
    if (selections.post_event_report && preflight?.post_event_report_available) {
      steps.push({
        label: 'Post Event Report',
        run: async () => {
          // PER POST requires apply_refunds + expenses — re-use persisted.
          const persisted = await axios.get(
            `${API}/post-event-report/${competitionId}/expenses`,
            { withCredentials: true }
          );
          const blob = await fetchBlob(
            `${API}/post-event-report/${competitionId}/generate`,
            undefined, 'POST',
            { apply_refunds: persisted.data?.apply_refunds ?? true,
              expenses: persisted.data?.expenses || [] },
          );
          // PER endpoint returns a ZIP — unwrap so the inner xlsx + pdf
          // sit at the archive root.
          await addBlobToZip(zip, `${filenameSafe(compName)} - Post Event Report.zip`, blob);
        },
      });
    }

    if (steps.length === 0) {
      toast.error('Pick at least one document to include.', { id: toastId });
      setGenerating(false);
      return;
    }

    try {
      for (let i = 0; i < steps.length; i++) {
        const s = steps[i];
        toast.loading(`Generating ${s.label} (${i + 1}/${steps.length})…`, { id: toastId });
        try {
          await s.run();
        } catch (err) {
          // Don't kill the whole pack — note the failure and continue.
          const msg = err.response?.data?.detail || err.message || 'unknown error';
          zip.file(
            `_FAILED - ${filenameSafe(s.label)}.txt`,
            `Failed to generate "${s.label}": ${msg}`,
          );
          toast.warning(`${s.label} failed (${msg}) — pack will continue without it.`);
        }
      }

      toast.loading('Building zip archive…', { id: toastId });
      const blob = await zip.generateAsync({ type: 'blob' });

      // Trigger download.
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = archiveFilename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Archive ready — ${archiveFilename}`, { id: toastId, duration: 5000 });

      // Record the archive event so the panel can show "Last
      // archived on …" and offer a re-archive shortcut. Non-fatal
      // if the record call fails — the download already worked.
      const itemsIncluded = steps.map((s) => s.label);
      try {
        await axios.post(
          `${API}/documents/summary-pack/${competitionId}/record`,
          { items_included: itemsIncluded, filename: archiveFilename },
          { withCredentials: true }
        );
        setPreflight((p) => p ? {
          ...p,
          last_archive: {
            archived_at: new Date().toISOString(),
            archived_by: 'you',
            items_included: itemsIncluded,
            filename: archiveFilename,
          },
        } : p);
      } catch {
        /* non-fatal */
      }

      // Fire the callback so the parent can open the delete dialog.
      if (onArchiveCreated) onArchiveCreated();
    } catch (err) {
      toast.error(err.message || 'Failed to build summary pack', { id: toastId });
    } finally {
      setGenerating(false);
    }
  };

  if (!competitionId) {
    return (
      <div className="text-sm text-stone-600">
        Select a completed competition to generate its summary pack.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-stone-600">
        <Loader2 className="h-4 w-4 animate-spin" /> Checking competition status…
      </div>
    );
  }

  const status = preflight?.competition_status;
  if (status !== 'Completed') {
    return (
      <div className="space-y-3" data-testid="summary-pack-not-completed">
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          The Competition Summary Pack is only available once the competition&apos;s
          status has been set to <strong>Completed</strong>.
          {status ? <> Current status: <strong>{status}</strong>.</> : null}
        </div>
        <p className="text-xs text-stone-600">
          You can change the status from <em>Competition Management → Edit</em>.
        </p>
      </div>
    );
  }

  const items = [
    { key: 'event_notice', label: 'Event Notice', note: 'Regenerated from the current settings.' },
    { key: 'programme', label: 'Print Version of the Programme', note: 'Regenerated from the current Order of Skate + Final Programme settings.' },
    { key: 'entry_summary', label: 'Entry Summary (all clubs)', note: 'Excel populated with every entry in the competition.' },
    {
      key: 'results',
      label: 'Summary of Results',
      gated: !preflight?.results_available,
      note: preflight?.results_available
        ? 'Includes all entered placings.'
        : 'No results have been entered yet — skipping.',
    },
    {
      key: 'post_event_report',
      label: 'Post Event Report',
      gated: !preflight?.post_event_report_available,
      note: preflight?.post_event_report_available
        ? 'Uses the persisted expenses.'
        : 'No expenses have been entered yet — skipping.',
    },
  ];

  // Map between the panel's item keys and the labels persisted on the
  // last-archive record. Lets the re-archive shortcut restore the
  // exact selection set the user used last time.
  const labelToKey = {
    'Event Notice': 'event_notice',
    'Print Programme': 'programme',
    'Entry Summary': 'entry_summary',
    'Summary of Results': 'results',
    'Post Event Report': 'post_event_report',
  };

  const reArchiveWithSameItems = () => {
    const lastItems = preflight?.last_archive?.items_included || [];
    if (lastItems.length === 0) return;
    const next = { event_notice: false, programme: false, entry_summary: false,
                   results: false, post_event_report: false };
    for (const label of lastItems) {
      const k = labelToKey[label];
      if (k) next[k] = true;
    }
    // Honour current gating so we don't try to tick something we
    // can't generate now.
    if (!preflight?.results_available) next.results = false;
    if (!preflight?.post_event_report_available) next.post_event_report = false;
    setSelections(next);
    // Slight delay so the UI shows the new selection before generate
    // starts (improves perceived feedback).
    setTimeout(() => { void generate(); }, 50);
  };

  const formatArchivedAt = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  const lastArchive = preflight?.last_archive;

  return (
    <div className="space-y-6 max-h-[calc(100vh-280px)] overflow-y-auto pr-2"
         data-testid="summary-pack-panel">
      <div>
        <h3 className="text-lg font-semibold text-stone-800 mb-1 flex items-center gap-2">
          <Archive className="h-5 w-5 text-[#5C4033]" />
          Generate Competition Archive
        </h3>
        <p className="text-sm text-muted-foreground">
          Generate files to download key information about this competition.
        </p>
      </div>

      {lastArchive && (
        <div
          className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-start justify-between gap-3"
          data-testid="summary-pack-last-archive-banner"
        >
          <div className="text-sm text-emerald-900">
            <div className="font-medium">
              Last archived on {formatArchivedAt(lastArchive.archived_at)}
            </div>
            <div className="text-xs text-emerald-800 mt-0.5">
              {(lastArchive.items_included || []).join(', ') || '(no items recorded)'}
            </div>
            {lastArchive.filename && (
              <div className="text-xs text-emerald-700/80 mt-0.5 font-mono break-all">
                {lastArchive.filename}
              </div>
            )}
          </div>
          <Button
            size="sm"
            variant="outline"
            disabled={generating}
            onClick={reArchiveWithSameItems}
            className="border-emerald-400 text-emerald-800 hover:bg-emerald-100"
            data-testid="summary-pack-rearchive-btn"
          >
            Re-archive with same items
          </Button>
        </div>
      )}

      <div className="space-y-3">
        {items.map((it) => (
          <div
            key={it.key}
            className={`flex items-start gap-3 p-3 rounded-lg border ${
              it.gated ? 'bg-stone-50 border-stone-200 opacity-60' : 'bg-white border-stone-200'
            }`}
            data-testid={`summary-pack-item-${it.key}`}
          >
            <Checkbox
              id={`pack-${it.key}`}
              checked={selections[it.key] && !it.gated}
              disabled={it.gated}
              onCheckedChange={() => toggle(it.key)}
              data-testid={`summary-pack-checkbox-${it.key}`}
            />
            <div className="flex-1">
              <Label
                htmlFor={`pack-${it.key}`}
                className={`font-medium ${it.gated ? 'text-stone-500' : 'text-stone-800'} cursor-pointer`}
              >
                {it.label}
              </Label>
              <p className={`text-xs mt-0.5 ${it.gated ? 'text-stone-500' : 'text-stone-600'}`}>
                {it.note}
              </p>
            </div>
          </div>
        ))}
      </div>

      <Button
        onClick={generate}
        disabled={generating || !competitionId}
        className="bg-[#5C4033] hover:bg-[#3d2a22] text-white w-full"
        data-testid="summary-pack-generate-btn"
      >
        {generating
          ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Building archive…</>
          : <><Download className="h-4 w-4 mr-2" /> Generate &amp; Download Archive</>}
      </Button>
    </div>
  );
}
